import * as React from 'react'
import { GripVertical, X, Plus, Pencil, Minus, Copy, MoreHorizontal, Search } from 'lucide-react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import { Button } from '@/components/ui/button'
import { SafeHtml } from '@/components/shared/safe-html'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { Item, TaxRate } from '@/gen/cortado/v1/invoices_pb'
import { ItemUnitEnum } from '@/gen/cortado/v1/settings_pb'
import { formatEnumName } from '@/lib/enum-utils'
import { cn } from '@/lib/utils'
import { centsToDollars, dollarsToCents, formatCurrency, formatDollars, formatTaxRate, formatTaxRateFraction } from '@/lib/currency-utils'
import { applyTaxRateToLineItem, calculateLineTotalCents } from '@/lib/line-item-calculations'

import { Input } from '@/components/ui/input'
import { CurrencyInput } from '@/components/ui/currency-input'
import { QuoteLineItemEditSheet } from './quote-line-item-edit-sheet'
import {
  QUOTE_ITEM_NO_TAX_VALUE,
  QUOTE_ITEM_PRICE_INPUT_CLASS,
  QuoteItemInlineSelect,
  QuoteItemDragHandle,
  QuoteItemList,
  QuoteItemListContainer,
  QuoteItemQuantityStepper,
  QuoteItemTwoLineRow,
  buildQuoteItemTaxOptions,
  buildQuoteItemUnitOptions,
  formatQuoteItemTaxSubLine,
  formatQuoteItemUnitLabel,
  getQuoteItemQuantityIncrement,
} from './quote-item-table'

export interface QuoteLineItem {
  id?: string // Optional - only exists for saved quote revision line items
  sourceItemId?: string // Optional - references items.id (null for custom one-time items)
  tempId?: string // Temporary ID for custom items (used for drag-and-drop and updates)
  name: string
  description: string
  quantity: number // double in proto
  unitPriceCents: number // int64 in proto - stored as cents
  unit: ItemUnitEnum // Store enum value (e.g. ItemUnitEnum.ITEM_UNIT_PER_HOUR = 2)
  sortOrder: number
  lineTotalCents?: number // Optional - pre-calculated by backend for saved items

  // Per-line-item sales tax fields
  isTaxable: boolean
  salesTaxRateId?: string
  salesTaxRateName?: string
  salesTaxRate?: number  // 0.0825 for 8.25% (decimal format)
  salesTaxCents: number // Required - calculated from tax rate * line total

  // Optional - an existing line ↔ quote event link, passed through unchanged on
  // save by the standalone quote page's builder. Nothing creates new links.
  quoteRevisionEventId?: string
}

// Helper function to get a unique ID for a line item (catalog or custom).
// Prefers the DB id (saved revisions), then tempId (in-flight rows).
// sourceItemId is NOT a unique key because multiple line items can reference
// the same catalog item — falling back to it caused duplicate React/dnd-kit
// keys and made delete remove every row sharing a sourceItemId.
function getLineItemId(item: QuoteLineItem): string {
  return item.id || item.tempId || `temp-${item.sortOrder}`
}

// Helper function to calculate sales tax for a line item (with discount ratio applied)
function calculateLineItemTax(lineTotalCents: number, taxRate: number, isTaxable: boolean, discountRatio = 1): number {
  if (!isTaxable || !taxRate) return 0
  const lineAfterDiscount = Math.round(lineTotalCents * discountRatio)
  return Math.round(lineAfterDiscount * taxRate)
}

interface QuoteLineItemsTableProps {
  items: QuoteLineItem[]
  availableItems: Item[]
  availableTaxRates: TaxRate[]
  onItemsChange: (items: QuoteLineItem[]) => void
  disabled?: boolean
  /** Preserve persisted amounts in read-only revisions; drafts recalculate. */
  useStoredTotals?: boolean
  /**
   * `rows` is the quote builder's two-line row list. `table` and `cards` are
   * the legacy invoice presentations and are unchanged.
   */
  presentation?: 'table' | 'cards' | 'rows'
  quoteId?: string // Optional - only provided for existing quotes
  discountRatio?: number // Ratio of (subtotal - discount) / subtotal for tax display
  discountAmountCents?: number // Absolute discount amount in cents (for summary display)
  // When set, "Apply Sales Tax to All Items" is engaged for this tax rate id -
  // every newly added item (catalog, custom, duplicate) is created taxable with it.
  autoApplyTaxRateId?: string
  // Fired when the user manually sets an existing item's tax to something other
  // than autoApplyTaxRateId, signalling the "apply to all" state no longer holds.
  onAutoApplyOverride?: () => void
}

function SortableRow({
  item,
  lineTotalCents,
  onEdit,
  onRemove,
  onDuplicate,
  onQuantityChange,
  onUnitChange,
  onTaxRateChange,
  availableTaxRates,
  disabled,
  discountRatio = 1,
}: {
  item: QuoteLineItem
  lineTotalCents: number
  onEdit: () => void
  onRemove: () => void
  onDuplicate: () => void
  onQuantityChange: (newQuantity: number) => void
  onUnitChange: (newUnit: ItemUnitEnum) => void
  onTaxRateChange: (taxRateId: string | undefined) => void
  availableTaxRates: TaxRate[]
  disabled?: boolean
  discountRatio?: number
}) {
  const [isHovered, setIsHovered] = React.useState(false)
  const [isUnitDropdownOpen, setIsUnitDropdownOpen] = React.useState(false)
  const [isTaxRateDropdownOpen, setIsTaxRateDropdownOpen] = React.useState(false)
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: getLineItemId(item) })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const isTransportation = item.unit === ItemUnitEnum.ITEM_UNIT_PER_MILE

  // Determine increment amount based on unit type
  const increment = item.unit === ItemUnitEnum.ITEM_UNIT_PER_HOUR
    ? 0.5
    : item.unit === ItemUnitEnum.ITEM_UNIT_PER_MILE
      ? 0.1
      : 1

  const handleDecrement = (e: React.MouseEvent) => {
    e.stopPropagation()
    const newQuantity = Math.max(increment, Math.round((item.quantity - increment) * 10) / 10)
    onQuantityChange(newQuantity)
  }

  const handleIncrement = (e: React.MouseEvent) => {
    e.stopPropagation()
    const newQuantity = Math.round((item.quantity + increment) * 10) / 10
    onQuantityChange(newQuantity)
  }

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      className={cn(
        isDragging && 'bg-muted',
        !disabled && 'cursor-pointer hover:bg-muted/50'
      )}
      onClick={!disabled ? onEdit : undefined}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          {...attributes}
          {...listeners}
          className={cn(
            "text-muted-foreground hover:text-foreground",
            disabled ? "cursor-not-allowed" : "cursor-grab active:cursor-grabbing"
          )}
          disabled={disabled}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      </TableCell>
      <TableCell className="min-w-[200px]">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="font-medium">{item.name || 'Unnamed Item'}</span>
            {isHovered && !disabled && (
              <Pencil className="h-3 w-3 text-muted-foreground" />
            )}
          </div>
          {item.description && (
            <SafeHtml
              html={item.description}
              className="text-sm text-muted-foreground line-clamp-2"
            />
          )}
        </div>
      </TableCell>
      <TableCell className="text-center">
        ${formatCurrency(item.unitPriceCents)}
      </TableCell>
      <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={handleDecrement}
            disabled={disabled || item.quantity <= increment}
          >
            <Minus className="h-3 w-3" />
            <span className="sr-only">Decrease quantity</span>
          </Button>
          <span className="min-w-[3ch] text-center">
            {item.quantity % 1 === 0 ? item.quantity : item.quantity.toFixed(1)}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={handleIncrement}
            disabled={disabled}
          >
            <Plus className="h-3 w-3" />
            <span className="sr-only">Increase quantity</span>
          </Button>
        </div>
      </TableCell>
      <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
        <DropdownMenu open={isUnitDropdownOpen} onOpenChange={setIsUnitDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              disabled={disabled || isTransportation}
              className={cn(
                "text-sm cursor-pointer px-2 py-1 rounded hover:bg-accent hover:text-accent-foreground transition-colors",
                disabled && "cursor-not-allowed opacity-50"
              )}
            >
              {formatEnumName(item.unit, ItemUnitEnum)}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem
              onSelect={() => {
                onUnitChange(ItemUnitEnum.ITEM_UNIT_PER_EVENT)
                setIsUnitDropdownOpen(false)
              }}
            >
              {formatEnumName(ItemUnitEnum.ITEM_UNIT_PER_EVENT, ItemUnitEnum)}
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => {
                onUnitChange(ItemUnitEnum.ITEM_UNIT_PER_HOUR)
                setIsUnitDropdownOpen(false)
              }}
            >
              {formatEnumName(ItemUnitEnum.ITEM_UNIT_PER_HOUR, ItemUnitEnum)}
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => {
                onUnitChange(ItemUnitEnum.ITEM_UNIT_PER_DAY)
                setIsUnitDropdownOpen(false)
              }}
            >
              {formatEnumName(ItemUnitEnum.ITEM_UNIT_PER_DAY, ItemUnitEnum)}
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => {
                onUnitChange(ItemUnitEnum.ITEM_UNIT_PER_UNIT)
                setIsUnitDropdownOpen(false)
              }}
            >
              {formatEnumName(ItemUnitEnum.ITEM_UNIT_PER_UNIT, ItemUnitEnum)}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
      <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
        <DropdownMenu open={isTaxRateDropdownOpen} onOpenChange={setIsTaxRateDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              disabled={disabled || isTransportation}
              className={cn(
                "text-sm cursor-pointer px-2 py-1 rounded hover:bg-accent hover:text-accent-foreground transition-colors",
                disabled && "cursor-not-allowed opacity-50"
              )}
            >
              {item.isTaxable && item.salesTaxRateName ? (
                <div className="space-y-1">
                  <div>{formatTaxRateFraction(item.salesTaxRate || 0)}%</div>
                  <div className="text-xs text-muted-foreground">
                    {item.salesTaxRateName}
                  </div>
                </div>
              ) : (
                'No Tax'
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem
              onSelect={() => {
                onTaxRateChange(undefined)
                setIsTaxRateDropdownOpen(false)
              }}
            >
              No Tax
            </DropdownMenuItem>
            {availableTaxRates.map((taxRate) => (
              <DropdownMenuItem
                key={taxRate.id}
                onSelect={() => {
                  onTaxRateChange(taxRate.id)
                  setIsTaxRateDropdownOpen(false)
                }}
              >
                {taxRate.name} ({formatTaxRate(taxRate.percentage)}%)
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
      <TableCell className="text-right font-medium w-32">
        <div className="space-y-1">
          {discountRatio < 1 ? (
            <>
              <div className="text-xs text-muted-foreground line-through">
                ${formatCurrency(lineTotalCents)}
              </div>
              <div>${formatCurrency(Math.round(lineTotalCents * discountRatio))}</div>
            </>
          ) : (
            <div>${formatCurrency(lineTotalCents)}</div>
          )}
          {item.isTaxable && item.salesTaxRate && (
            <div className="text-xs text-muted-foreground">
              +${formatCurrency(Math.round(Math.round(lineTotalCents * discountRatio) * item.salesTaxRate))} tax
            </div>
          )}
        </div>
      </TableCell>
      <TableCell className="w-20" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-end gap-1">
          <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={onDuplicate}
          disabled={disabled || isTransportation}
        >
          <Copy className="h-4 w-4" />
          <span className="sr-only">Duplicate item</span>
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-destructive hover:text-destructive"
          onClick={onRemove}
          disabled={disabled}
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Remove item</span>
        </Button>
        </div>
      </TableCell>
    </TableRow>
  )
}

function SortableCard({
  item,
  lineTotalCents,
  onEdit,
  onRemove,
  onDuplicate,
  onQuantityChange,
  onUnitChange,
  onTaxRateChange,
  availableTaxRates,
  disabled,
  discountRatio = 1,
}: {
  item: QuoteLineItem
  lineTotalCents: number
  onEdit: () => void
  onRemove: () => void
  onDuplicate: () => void
  onQuantityChange: (newQuantity: number) => void
  onUnitChange: (newUnit: ItemUnitEnum) => void
  onTaxRateChange: (taxRateId: string | undefined) => void
  availableTaxRates: TaxRate[]
  disabled?: boolean
  discountRatio?: number
}) {
  const [isUnitDropdownOpen, setIsUnitDropdownOpen] = React.useState(false)
  const [isTaxRateDropdownOpen, setIsTaxRateDropdownOpen] = React.useState(false)
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: getLineItemId(item) })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }
  const increment = item.unit === ItemUnitEnum.ITEM_UNIT_PER_HOUR
    ? 0.5
    : item.unit === ItemUnitEnum.ITEM_UNIT_PER_MILE
      ? 0.1
      : 1
  const itemName = item.name || 'Unnamed Item'
  const isTransportation = item.unit === ItemUnitEnum.ITEM_UNIT_PER_MILE

  return (
    <li ref={setNodeRef} style={style}>
      <article className={cn('rounded-lg border bg-card p-3', isDragging && 'bg-muted')}>
        <div className="flex flex-wrap items-start gap-2">
          <button
            type="button"
            {...attributes}
            {...listeners}
            aria-label={`Reorder ${itemName}`}
            className={cn(
              'flex size-11 shrink-0 items-center justify-center rounded-md text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              disabled ? 'cursor-not-allowed opacity-50' : 'cursor-grab hover:bg-muted hover:text-foreground active:cursor-grabbing',
            )}
            disabled={disabled}
          >
            <GripVertical className="h-4 w-4" />
          </button>

          <div className="min-w-40 flex-1 py-1.5">
            <p className="font-medium leading-5">{itemName}</p>
            {item.description && (
              <SafeHtml
                html={item.description}
                className="mt-1 text-sm text-muted-foreground line-clamp-2 whitespace-normal"
              />
            )}
          </div>

          <div className="ml-auto flex flex-wrap gap-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="min-h-11"
              onClick={onEdit}
              disabled={disabled}
            >
              <Pencil className="h-4 w-4" />
              Edit
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="min-h-11"
              onClick={onDuplicate}
              disabled={disabled || isTransportation}
            >
              <Copy className="h-4 w-4" />
              Duplicate
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="min-h-11 text-destructive hover:text-destructive"
              onClick={onRemove}
              disabled={disabled}
            >
              <X className="h-4 w-4" />
              Remove
            </Button>
          </div>
        </div>

        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 border-t pt-3 @[44rem]/quote-items:grid-cols-5">
          <div className="min-w-0">
            <dt className="text-xs font-medium text-muted-foreground">Unit price</dt>
            <dd className="mt-1 font-financial text-sm tabular-nums">
              ${formatCurrency(item.unitPriceCents)}
            </dd>
          </div>

          <div className="min-w-0">
            <dt className="text-xs font-medium text-muted-foreground">Quantity</dt>
            <dd className="mt-1 flex min-h-11 items-center">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="size-11"
                aria-label={`Decrease quantity for ${itemName}`}
                onClick={() => onQuantityChange(Math.max(increment, Math.round((item.quantity - increment) * 10) / 10))}
                disabled={disabled || item.quantity <= increment}
              >
                <Minus className="h-4 w-4" />
              </Button>
              <span className="min-w-12 text-center text-sm tabular-nums">
                {item.quantity % 1 === 0 ? item.quantity : item.quantity.toFixed(1)}
              </span>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="size-11"
                aria-label={`Increase quantity for ${itemName}`}
                onClick={() => onQuantityChange(Math.round((item.quantity + increment) * 10) / 10)}
                disabled={disabled}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </dd>
          </div>

          <div className="min-w-0">
            <dt className="text-xs font-medium text-muted-foreground">Unit</dt>
            <dd className="mt-1">
              <DropdownMenu open={isUnitDropdownOpen} onOpenChange={setIsUnitDropdownOpen}>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="min-h-11 w-full justify-start whitespace-normal text-left"
                    disabled={disabled || isTransportation}
                  >
                    {formatEnumName(item.unit, ItemUnitEnum)}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {[
                    ItemUnitEnum.ITEM_UNIT_PER_EVENT,
                    ItemUnitEnum.ITEM_UNIT_PER_HOUR,
                    ItemUnitEnum.ITEM_UNIT_PER_DAY,
                    ItemUnitEnum.ITEM_UNIT_PER_UNIT,
                  ].map((unit) => (
                    <DropdownMenuItem
                      key={unit}
                      onSelect={() => {
                        onUnitChange(unit)
                        setIsUnitDropdownOpen(false)
                      }}
                    >
                      {formatEnumName(unit, ItemUnitEnum)}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </dd>
          </div>

          <div className="min-w-0">
            <dt className="text-xs font-medium text-muted-foreground">Tax rate</dt>
            <dd className="mt-1">
              <DropdownMenu open={isTaxRateDropdownOpen} onOpenChange={setIsTaxRateDropdownOpen}>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="min-h-11 w-full justify-start whitespace-normal text-left"
                    disabled={disabled}
                  >
                    {item.isTaxable && item.salesTaxRateName
                      ? `${item.salesTaxRateName} (${formatTaxRateFraction(item.salesTaxRate || 0)}%)`
                      : 'No tax'}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem
                    onSelect={() => {
                      onTaxRateChange(undefined)
                      setIsTaxRateDropdownOpen(false)
                    }}
                  >
                    No tax
                  </DropdownMenuItem>
                  {availableTaxRates.map((taxRate) => (
                    <DropdownMenuItem
                      key={taxRate.id}
                      onSelect={() => {
                        onTaxRateChange(taxRate.id)
                        setIsTaxRateDropdownOpen(false)
                      }}
                    >
                      {taxRate.name} ({formatTaxRate(taxRate.percentage)}%)
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </dd>
          </div>

          <div className="min-w-0">
            <dt className="text-xs font-medium text-muted-foreground">Line total</dt>
            <dd className="mt-1 font-financial text-sm font-semibold tabular-nums">
              {discountRatio < 1 && (
                <span className="mr-2 text-xs font-normal text-muted-foreground line-through">
                  ${formatCurrency(lineTotalCents)}
                </span>
              )}
              ${formatCurrency(Math.round(lineTotalCents * discountRatio))}
              {item.isTaxable && item.salesTaxRate !== undefined && item.salesTaxRate > 0 ? (
                <span className="mt-1 block text-xs font-normal text-muted-foreground">
                  +${formatCurrency(Math.round(Math.round(lineTotalCents * discountRatio) * item.salesTaxRate))} tax
                </span>
              ) : null}
            </dd>
          </div>
        </dl>
      </article>
    </li>
  )
}

type QuoteLineItemRowActions = {
  onEdit: () => void
  onRemove: () => void
  onDuplicate: () => void
  onQuantityChange: (quantity: number) => void
  onUnitPriceChange: (unitPriceCents: number) => void
  onUnitChange: (unit: ItemUnitEnum) => void
  onTaxRateChange: (taxRateId: string | undefined) => void
}

/** Always-live unit-price field. */
function QuoteLineItemPriceInput({
  item,
  disabled,
  onUnitPriceChange,
}: {
  item: QuoteLineItem
  disabled?: boolean
  onUnitPriceChange: (unitPriceCents: number) => void
}) {
  return (
    <CurrencyInput
      value={centsToDollars(item.unitPriceCents)}
      onChange={(dollars) => onUnitPriceChange(dollarsToCents(dollars))}
      disabled={disabled}
      aria-label={`Unit price for ${item.name || 'Unnamed Item'}`}
      // w-36 fits `$125,000.00` at this font including the `$` adornment's
      // padding; the numeral is right-aligned so significant digits survive
      // anything wider, and lines up with the Total column beside it.
      className={QUOTE_ITEM_PRICE_INPUT_CLASS}
    />
  )
}

/** Single overflow menu — name, description, unit and tax are edited in the sheet. */
function QuoteLineItemOverflowMenu({
  itemName,
  actions,
  disabled,
  canDuplicate,
}: {
  itemName: string
  actions: QuoteLineItemRowActions
  disabled?: boolean
  canDuplicate: boolean
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-7 shrink-0 text-muted-foreground"
          aria-label={`Actions for ${itemName}`}
          disabled={disabled}
        >
          <MoreHorizontal className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={actions.onEdit}>Edit</DropdownMenuItem>
        {canDuplicate && (
          <DropdownMenuItem onSelect={actions.onDuplicate}>Duplicate</DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={actions.onRemove} variant="destructive">Remove</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** Quote builder row: two lines, with quantity and price always live inline. */
function QuoteBuilderItemRow({
  item,
  lineTotalCents,
  actions,
  availableTaxRates,
  disabled,
  discountRatio = 1,
}: {
  item: QuoteLineItem
  lineTotalCents: number
  actions: QuoteLineItemRowActions
  availableTaxRates: TaxRate[]
  disabled?: boolean
  discountRatio?: number
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: getLineItemId(item) })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }
  const itemName = item.name || 'Unnamed Item'
  const isTransportation = item.unit === ItemUnitEnum.ITEM_UNIT_PER_MILE
  // On a single-quantity, undiscounted row the line total is the unit price
  // the field below already shows, so the row prints the value once.
  const displayTotalCents = Math.round(lineTotalCents * discountRatio)

  return (
    <li ref={setNodeRef} style={style} className={cn(isDragging && 'bg-muted')}>
      <QuoteItemTwoLineRow
        dragHandle={
          <QuoteItemDragHandle
            itemName={itemName}
            attributes={attributes}
            listeners={listeners}
            disabled={disabled}
          />
        }
        name={itemName}
        totalLabel={displayTotalCents === item.unitPriceCents
          ? undefined
          : `$${formatCurrency(displayTotalCents)}`}
        quantityControl={
          <QuoteItemQuantityStepper
            itemName={itemName}
            quantity={item.quantity}
            increment={getQuoteItemQuantityIncrement(item.unit)}
            disabled={disabled}
            onQuantityChange={actions.onQuantityChange}
          />
        }
        metaItems={[
          isTransportation ? formatQuoteItemUnitLabel(item.unit) : (
            <QuoteItemInlineSelect
              label={`Change unit for ${itemName}`}
              triggerLabel={formatQuoteItemUnitLabel(item.unit)}
              value={String(item.unit)}
              options={buildQuoteItemUnitOptions()}
              disabled={disabled}
              onSelect={(value) => actions.onUnitChange(Number(value) as ItemUnitEnum)}
            />
          ),
          <QuoteItemInlineSelect
            label={`Change tax rate for ${itemName}`}
            triggerLabel={formatQuoteItemTaxSubLine(item)}
            value={item.isTaxable && item.salesTaxRateId ? item.salesTaxRateId : QUOTE_ITEM_NO_TAX_VALUE}
            options={buildQuoteItemTaxOptions(availableTaxRates)}
            disabled={disabled}
            onSelect={(value) => actions.onTaxRateChange(value === QUOTE_ITEM_NO_TAX_VALUE ? undefined : value)}
          />,
        ]}
        priceControl={
          <QuoteLineItemPriceInput
            item={item}
            disabled={disabled}
            onUnitPriceChange={actions.onUnitPriceChange}
          />
        }
        menu={
          <QuoteLineItemOverflowMenu
            itemName={itemName}
            actions={actions}
            disabled={disabled}
            canDuplicate={!isTransportation}
          />
        }
      />
    </li>
  )
}

function QuoteBuilderAddItemRow({
  availableItems,
  disabled,
  onAddCatalogItem,
  onAddCustomItem,
}: {
  availableItems: Item[]
  disabled?: boolean
  onAddCatalogItem: (item: Item) => void
  onAddCustomItem: (name: string) => void
}) {
  const [query, setQuery] = React.useState('')
  const trimmed = query.trim()
  const matches = trimmed
    ? availableItems
        .filter((candidate) => candidate.title.toLowerCase().includes(trimmed.toLowerCase()))
        .slice(0, 5)
    : []

  const handleAdd = () => {
    const exact = matches.find((candidate) => candidate.title.toLowerCase() === trimmed.toLowerCase())
    if (exact) {
      onAddCatalogItem(exact)
    } else {
      onAddCustomItem(trimmed)
    }
    setQuery('')
  }

  return (
    <div className="border-t p-2">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                handleAdd()
              }
            }}
            disabled={disabled}
            aria-label="Search catalog or type a custom item"
            placeholder="Search catalog or type a custom item…"
            className="h-9 border-0 pl-9 shadow-none focus-visible:ring-0"
          />
        </div>
        <Button type="button" variant="outline" size="sm" onClick={handleAdd} disabled={disabled}>
          <Plus className="size-4" />
          Add
        </Button>
      </div>
      {matches.length > 0 && (
        <ul className="mt-2 space-y-1">
          {matches.map((match) => (
            <li key={match.id}>
              <button
                type="button"
                disabled={disabled}
                onClick={() => {
                  onAddCatalogItem(match)
                  setQuery('')
                }}
                className="flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-sm hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
              >
                <span className="truncate">{match.title}</span>
                <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                  ${formatDollars(match.price)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export function QuoteLineItemsTable({
  items,
  availableItems,
  availableTaxRates,
  onItemsChange,
  disabled = false,
  useStoredTotals = false,
  presentation = 'table',
  discountRatio = 1,
  discountAmountCents = 0,
  autoApplyTaxRateId,
  onAutoApplyOverride,
}: QuoteLineItemsTableProps) {
  const [open, setOpen] = React.useState(false)
  const [editingItem, setEditingItem] = React.useState<QuoteLineItem | null>(null)
  const addItemRef = React.useRef<HTMLDivElement>(null)
  // `rows` is the quote builder's layout: one two-line row at every width.
  // The explicit legacy values keep the invoice presentations untouched.
  const isBuilderLayout = presentation === 'rows'

  // Close the Add Item picker on outside click or Escape. It renders inline
  // (no Portal) so it stays scrollable inside a Sheet's scroll lock.
  React.useEffect(() => {
    if (!open) return
    function handleClickOutside(e: MouseEvent) {
      if (addItemRef.current && !addItemRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [open])

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: disabled ? 999999 : 0, // Prevent drag when disabled
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    // Don't allow reordering if disabled
    if (disabled) return

    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => getLineItemId(item) === active.id)
      const newIndex = items.findIndex((item) => getLineItemId(item) === over.id)

      const newItems = arrayMove(items, oldIndex, newIndex)
      // Update sortOrder numbers
      const reorderedItems = newItems.map((item, index) => ({
        ...item,
        sortOrder: index,
      }))
      onItemsChange(reorderedItems)
    }
  }

  // While "Apply Sales Tax to All Items" is engaged, stamp its tax rate onto a
  // freshly created item so every add path stays consistent with existing items.
  const applyAutoTax = (newItem: QuoteLineItem): QuoteLineItem => {
    const autoTaxRate = autoApplyTaxRateId
      ? availableTaxRates.find((rate) => rate.id === autoApplyTaxRateId)
      : undefined
    if (!autoTaxRate) return newItem

    const lineTotalCents = calculateLineTotalCents(newItem.quantity, newItem.unitPriceCents)
    const salesTaxRate = autoTaxRate.percentage / 100
    return {
      ...newItem,
      isTaxable: true,
      salesTaxRateId: autoTaxRate.id,
      salesTaxRateName: autoTaxRate.name,
      salesTaxRate,
      salesTaxCents: Math.round(lineTotalCents * salesTaxRate),
    }
  }

  const handleAddItem = (selectedItem: Item) => {
    const newItem: QuoteLineItem = {
      tempId: crypto.randomUUID(),
      sourceItemId: selectedItem.id,
      name: selectedItem.title,
      description: selectedItem.description || '',
      quantity: 1,
      unitPriceCents: dollarsToCents(selectedItem.price),
      unit: selectedItem.unit || ItemUnitEnum.ITEM_UNIT_PER_EVENT,
      sortOrder: items.length,
      isTaxable: true, // Default to taxable
      salesTaxCents: 0, // No tax until rate is selected
    }
    onItemsChange([...items, applyAutoTax(newItem)])
    setOpen(false)
  }

  const handleAddCustomItem = (name = '') => {
    // Open sheet with empty item for creation
    const newItem: QuoteLineItem = {
      tempId: crypto.randomUUID(),
      name,
      description: '',
      quantity: 1,
      unitPriceCents: 0,
      unit: ItemUnitEnum.ITEM_UNIT_PER_EVENT,
      sortOrder: items.length,
      isTaxable: true,
      salesTaxCents: 0,
    }
    setEditingItem(applyAutoTax(newItem))
  }

  const handleSaveItem = (savedItem: QuoteLineItem) => {
    // Manually diverging one item from the auto-applied rate means "all items"
    // no longer holds. The builder layout edits tax in this sheet rather than a
    // per-row dropdown, so the signal has to fire here too.
    if (autoApplyTaxRateId && savedItem.salesTaxRateId !== autoApplyTaxRateId) {
      onAutoApplyOverride?.()
    }
    // Check this is a new item or an update
    const itemId = getLineItemId(savedItem)
    const existingIndex = items.findIndex((item) => getLineItemId(item) === itemId)

    if (existingIndex >= 0) {
      // Update existing item
      const newItems = items.map((item, index) =>
        index === existingIndex ? savedItem : item
      )
      onItemsChange(newItems)
    } else {
      // Add new item with proper sort order
      const newItem = {
        ...savedItem,
        sortOrder: items.length,
      }
      onItemsChange([...items, newItem])
    }
  }

  const handleRemoveItem = (itemId: string) => {
    const newItems = items
      .filter((item) => getLineItemId(item) !== itemId)
      .map((item, index) => ({ ...item, sortOrder: index }))
    onItemsChange(newItems)
  }

  const handleDuplicateItem = (itemId: string) => {
    const sourceIndex = items.findIndex((item) => getLineItemId(item) === itemId)
    if (sourceIndex === -1) return

    const duplicatedItem: QuoteLineItem = {
      ...items[sourceIndex],
      id: undefined,
      tempId: crypto.randomUUID(),
    }

    const newItems = [
      ...items.slice(0, sourceIndex + 1),
      duplicatedItem,
      ...items.slice(sourceIndex + 1),
    ].map((item, index) => ({ ...item, sortOrder: index }))
    onItemsChange(newItems)
  }

  const handleQuantityChange = (itemId: string, newQuantity: number) => {
    const newItems = items.map((item) => {
      if (getLineItemId(item) !== itemId) return item

      // Update quantity and recalculate line total and tax
      const lineTotalCents = calculateLineTotalCents(newQuantity, item.unitPriceCents)
      const salesTaxCents = calculateLineItemTax(lineTotalCents, item.salesTaxRate || 0, item.isTaxable)

      return {
        ...item,
        quantity: newQuantity,
        lineTotalCents,
        salesTaxCents,
      }
    })
    onItemsChange(newItems)
  }

  const handleUnitPriceChange = (itemId: string, newUnitPriceCents: number) => {
    const newItems = items.map((item) => {
      if (getLineItemId(item) !== itemId) return item

      const lineTotalCents = calculateLineTotalCents(item.quantity, newUnitPriceCents)
      const salesTaxCents = calculateLineItemTax(lineTotalCents, item.salesTaxRate || 0, item.isTaxable)

      return {
        ...item,
        unitPriceCents: newUnitPriceCents,
        lineTotalCents,
        salesTaxCents,
      }
    })
    onItemsChange(newItems)
  }

  const handleUnitChange = (itemId: string, newUnit: ItemUnitEnum) => {
    const newItems = items.map((item) => {
      if (getLineItemId(item) !== itemId) return item

      return {
        ...item,
        unit: newUnit,
      }
    })
    onItemsChange(newItems)
  }

  const handleTaxRateChange = (itemId: string, taxRateId: string | undefined) => {
    // Manually diverging one item from the auto-applied rate means "all items"
    // no longer holds - let the caller disengage it rather than keep taxing
    // future new items under a stale assumption.
    if (autoApplyTaxRateId && taxRateId !== autoApplyTaxRateId) {
      onAutoApplyOverride?.()
    }
    const selectedRate = taxRateId ? availableTaxRates.find((rate) => rate.id === taxRateId) : undefined
    onItemsChange(items.map((item) => (
      getLineItemId(item) === itemId ? applyTaxRateToLineItem(item, selectedRate) : item
    )))
  }

  const getLineTotal = (item: QuoteLineItem) => useStoredTotals && item.lineTotalCents !== undefined
    ? item.lineTotalCents
    : calculateLineTotalCents(item.quantity, item.unitPriceCents)
  const subtotalCents = items.reduce((sum, item) => sum + getLineTotal(item), 0)
  const taxTotalCents = items.reduce((sum, item) => {
    const lineTotal = getLineTotal(item)
    return sum + calculateLineItemTax(lineTotal, item.salesTaxRate || 0, item.isTaxable, discountRatio)
  }, 0)

  const buildRowActions = (item: QuoteLineItem): QuoteLineItemRowActions => ({
    onEdit: () => setEditingItem(item),
    onRemove: () => handleRemoveItem(getLineItemId(item)),
    onDuplicate: () => handleDuplicateItem(getLineItemId(item)),
    onQuantityChange: (quantity) => handleQuantityChange(getLineItemId(item), quantity),
    onUnitPriceChange: (unitPriceCents) => handleUnitPriceChange(getLineItemId(item), unitPriceCents),
    onUnitChange: (unit) => handleUnitChange(getLineItemId(item), unit),
    onTaxRateChange: (taxRateId) => handleTaxRateChange(getLineItemId(item), taxRateId),
  })

  if (isBuilderLayout) {
    return (
      <div className="space-y-4">
        <QuoteItemListContainer>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={items.map((item) => getLineItemId(item))}
              strategy={verticalListSortingStrategy}
            >
              <QuoteItemList>
                {items.length === 0 ? (
                  <li className="px-3 py-6 text-center text-sm text-muted-foreground">
                    No line items yet
                  </li>
                ) : items.map((item) => (
                  <QuoteBuilderItemRow
                    key={getLineItemId(item)}
                    item={item}
                    lineTotalCents={getLineTotal(item)}
                    actions={buildRowActions(item)}
                    availableTaxRates={availableTaxRates}
                    disabled={disabled}
                    discountRatio={discountRatio}
                  />
                ))}
              </QuoteItemList>
            </SortableContext>
          </DndContext>

          <QuoteBuilderAddItemRow
            availableItems={availableItems}
            disabled={disabled}
            onAddCatalogItem={handleAddItem}
            onAddCustomItem={handleAddCustomItem}
          />
        </QuoteItemListContainer>

        <QuoteLineItemEditSheet
          item={editingItem}
          availableItems={availableItems}
          availableTaxRates={availableTaxRates}
          open={!!editingItem}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) setEditingItem(null)
          }}
          onSave={handleSaveItem}
          disabled={disabled}
        />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-medium">Items</h3>
          <p className="text-xs text-muted-foreground">Manage items included in this quote</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => handleAddCustomItem()}
            disabled={disabled}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Custom Item
          </Button>
          <div className="relative" ref={addItemRef}>
            <Button
              type="button"
              size="sm"
              disabled={disabled || availableItems.length === 0}
              onClick={() => setOpen(!open)}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Item
            </Button>
            {open && (
              <div className="absolute right-0 top-full z-50 mt-1 w-[300px] rounded-md border bg-popover text-popover-foreground shadow-md">
                <Command>
                  <CommandInput placeholder="Search items..." autoFocus />
                  <CommandList>
                    <CommandEmpty>No items found.</CommandEmpty>
                    <CommandGroup>
                      {availableItems.map((item) => (
                        <CommandItem
                          key={item.id}
                          value={`${item.title} ${item.description || ''} ${item.id}`}
                          onSelect={() => handleAddItem(item)}
                          className="cursor-pointer"
                        >
                          <div className="flex flex-col">
                            <span className="font-medium">{item.title}</span>
                            {item.description && (
                              <SafeHtml
                                html={item.description}
                                className="text-xs text-muted-foreground line-clamp-1"
                              />
                            )}
                            <span className="text-xs text-muted-foreground">${formatDollars(item.price)}</span>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </div>
            )}
          </div>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed rounded-lg p-12 text-center">
          <p className="text-sm text-muted-foreground">No items added yet</p>
          <p className="text-xs text-muted-foreground mt-1">
            Click &quot;Add Item&quot; to get started
          </p>
        </div>
      ) : presentation === 'cards' ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <ul className="@container/quote-items space-y-3">
            <SortableContext items={items.map((item) => getLineItemId(item))} strategy={verticalListSortingStrategy}>
              {items.map((item) => (
                <SortableCard
                  key={getLineItemId(item)}
                  item={item}
                  lineTotalCents={getLineTotal(item)}
                  onEdit={() => setEditingItem(item)}
                  onRemove={() => handleRemoveItem(getLineItemId(item))}
                  onDuplicate={() => handleDuplicateItem(getLineItemId(item))}
                  onQuantityChange={(newQuantity) => handleQuantityChange(getLineItemId(item), newQuantity)}
                  onUnitChange={(newUnit) => handleUnitChange(getLineItemId(item), newUnit)}
                  onTaxRateChange={(taxRateId) => handleTaxRateChange(getLineItemId(item), taxRateId)}
                  availableTaxRates={availableTaxRates}
                  disabled={disabled}
                  discountRatio={discountRatio}
                />
              ))}
            </SortableContext>
          </ul>
        </DndContext>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Item & Description</TableHead>
                  <TableHead className="text-center">Price</TableHead>
                  <TableHead className="text-center">Quantity</TableHead>
                  <TableHead className="text-center">Unit</TableHead>
                  <TableHead className="text-center">Tax Rate</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="w-20"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <SortableContext items={items.map((item) => getLineItemId(item))} strategy={verticalListSortingStrategy}>
                  {items.map((item) => (
                    <SortableRow
                      key={getLineItemId(item)}
                      item={item}
                      lineTotalCents={getLineTotal(item)}
                      onEdit={() => setEditingItem(item)}
                      onRemove={() => handleRemoveItem(getLineItemId(item))}
                      onDuplicate={() => handleDuplicateItem(getLineItemId(item))}
                      onQuantityChange={(newQuantity) => handleQuantityChange(getLineItemId(item), newQuantity)}
                      onUnitChange={(newUnit) => handleUnitChange(getLineItemId(item), newUnit)}
                      onTaxRateChange={(taxRateId) => handleTaxRateChange(getLineItemId(item), taxRateId)}
                      availableTaxRates={availableTaxRates}
                      disabled={disabled}
                      discountRatio={discountRatio}
                    />
                  ))}
                </SortableContext>
              </TableBody>
            </Table>
          </DndContext>
        </div>
      )}

      {items.length > 0 && (
        <div className="flex justify-end">
          <div className="w-56 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="font-financial font-medium">${formatCurrency(subtotalCents)}</span>
            </div>
            {discountAmountCents > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Discount</span>
                <span className="font-financial font-medium text-status-success-fg">
                  -${formatCurrency(discountAmountCents)}
                </span>
              </div>
            )}
            {taxTotalCents > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Tax</span>
                <span className="font-financial font-medium">${formatCurrency(taxTotalCents)}</span>
              </div>
            )}
            {(taxTotalCents > 0 || discountAmountCents > 0) && (
              <div className="border-t-2 border-foreground pt-2 flex justify-between">
                <span className="font-semibold">Total</span>
                <span className="font-financial font-semibold">
                  ${formatCurrency(subtotalCents - discountAmountCents + taxTotalCents)}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit Sheet */}
      <QuoteLineItemEditSheet
        item={editingItem}
        availableItems={availableItems}
        availableTaxRates={availableTaxRates}
        open={!!editingItem}
        onOpenChange={(open) => {
          if (!open) setEditingItem(null)
        }}
        onSave={handleSaveItem}
        disabled={disabled}
      />
    </div>
  )
}
